import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Boxes, Send, Info, Lock } from "lucide-react";
import { toast } from "sonner";
import api, { fmtMXN } from "../lib/api";
import { useTranslation } from "react-i18next";

export default function POQuantityChangeModal({ poId, onClose, onSuccess }) {
  const { t } = useTranslation();
  const [po, setPo] = useState(null);
  const [qty, setQty] = useState({});
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/purchase-orders/${poId}`)
      .then((r) => {
        setPo(r.data);
        const init = {};
        (r.data.items || []).forEach((it) => { init[it.supply_id] = String(it.quantity); });
        setQty(init);
      })
      .catch((e) => {
        toast.error(e?.response?.data?.detail || t("common.error"));
        onClose();
      });
    // eslint-disable-next-line
  }, [poId]);

  const proposedTotal = useMemo(() => {
    if (!po) return 0;
    const subtotal = (po.items || []).reduce((acc, it) => {
      const q = parseFloat(qty[it.supply_id]);
      return acc + (isNaN(q) ? 0 : q) * Number(it.unit_price || 0);
    }, 0);
    const tax = subtotal * (Number(po.iva_rate ?? 16) / 100);
    const storedSubtotal = Number(po.subtotal || 0);
    const isrRate = storedSubtotal ? Number(po.isr_withholding_amount || 0) / storedSubtotal : 0;
    const ivaRetRate = storedSubtotal ? Number(po.iva_withholding_amount || 0) / storedSubtotal : 0;
    return subtotal + tax - subtotal * isrRate - subtotal * ivaRetRate;
  }, [po, qty]);

  const hasChange = useMemo(() => {
    if (!po) return false;
    return (po.items || []).some((it) => {
      const q = parseFloat(qty[it.supply_id]);
      return !isNaN(q) && Math.abs(q - Number(it.quantity)) > 0.0001;
    });
  }, [po, qty]);

  const hasZeroOrNegative = useMemo(() => {
    if (!po) return false;
    return (po.items || []).some((it) => {
      const q = parseFloat(qty[it.supply_id]);
      return isNaN(q) || q <= 0;
    });
  }, [po, qty]);

  const submit = async () => {
    if (reason.trim().length < 10) { toast.error(t("poQty.reasonError")); return; }
    if (!hasChange) { toast.error(t("poQty.noChangeError")); return; }
    if (hasZeroOrNegative) { toast.error(t("poQty.zeroQtyError")); return; }
    setSubmitting(true);
    try {
      await api.post(`/purchase-orders/${poId}/quantity-change`, {
        reason: reason.trim(),
        items: (po.items || []).map((it) => ({
          supply_id: it.supply_id,
          proposed_quantity: parseFloat(qty[it.supply_id]) || 0,
        })),
      });
      toast.success(t("poQty.submittedToast"));
      onSuccess?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  if (!po) return null;

  return (
    <AnimatePresence>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
          data-testid="po-qty-change-modal"
          className="bg-white rounded-2xl w-full max-w-2xl overflow-hidden max-h-[92vh] flex flex-col">
          <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-10 h-10 rounded-xl bg-[#FFDF00] flex items-center justify-center">
                <Boxes className="w-5 h-5 text-[#1A1A1A]" />
              </div>
              <div>
                <h2 className="font-display font-bold text-base">{t("poQty.modalTitle")}</h2>
                <p className="text-xs text-slate-500">{t("poQty.modalSubtitle", { folio: po.folio })}</p>
              </div>
            </div>
            <button onClick={onClose} data-testid="po-qty-close-btn" className="w-9 h-9 rounded-xl hover:bg-slate-100 flex items-center justify-center">
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="p-6 space-y-4 overflow-y-auto">
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex items-start gap-2">
              <Lock className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-blue-900">{t("po.priceLockNotice")}</div>
            </div>

            <div className="border border-slate-200 rounded-xl overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-3 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{t("po.item")}</th>
                    <th className="text-right px-3 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{t("po.tableOrdered")}</th>
                    <th className="text-right px-3 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{t("common.quantity")}</th>
                    <th className="text-right px-3 py-2 text-[10px] font-bold text-slate-500 uppercase tracking-wider">{t("po.unitPrice")}</th>
                  </tr>
                </thead>
                <tbody>
                  {(po.items || []).map((it) => (
                    <tr key={it.supply_id} className="border-b border-slate-100 last:border-0">
                      <td className="px-3 py-2.5 text-sm font-semibold text-slate-800">
                        {it.supply_name}
                        {it.unit ? <span className="text-xs text-slate-400 ml-1">({it.unit})</span> : null}
                      </td>
                      <td className="px-3 py-2.5 text-sm text-slate-500 text-right font-mono">{it.quantity}</td>
                      <td className="px-3 py-2.5 text-right">
                        <input data-testid={`po-qty-input-${it.supply_id}`} type="number" min="0.01" step="0.01"
                          value={qty[it.supply_id] ?? ""}
                          onChange={(e) => setQty((p) => ({ ...p, [it.supply_id]: e.target.value }))}
                          className="w-24 px-2 py-1.5 bg-white border-2 border-[#FFDF00] rounded-lg text-sm font-mono font-bold text-right focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                      </td>
                      <td className="px-3 py-2.5 text-sm text-right font-mono text-slate-400" title={t("po.priceLockNotice")}>
                        <span className="inline-flex items-center gap-1 justify-end">
                          <Lock className="w-3 h-3" /> {fmtMXN(it.unit_price)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-slate-50 rounded-xl p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("poQty.currentTotal")}</div>
                <div className="text-lg font-display font-extrabold font-mono" data-testid="po-qty-current-total">{fmtMXN(po.total)}</div>
              </div>
              <div className="bg-[#FFF9DB] border border-[#FFDF00] rounded-xl p-3">
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{t("poQty.proposedTotal")}</div>
                <div className="text-lg font-display font-extrabold font-mono" data-testid="po-qty-proposed-total">{fmtMXN(proposedTotal)}</div>
              </div>
            </div>

            <div>
              <label className="text-[11px] font-bold text-slate-500 uppercase tracking-wider mb-1.5 block">{t("poQty.reason")}</label>
              <textarea data-testid="po-qty-reason" value={reason} rows={3} onChange={(e) => setReason(e.target.value)}
                placeholder={t("poQty.reasonPh")}
                className="w-full px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
            </div>

            <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex items-start gap-2">
              <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
              <div className="text-xs text-slate-700">{t("poQty.howApprovalWorks")}</div>
            </div>
          </div>

          <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex items-center justify-end gap-2">
            <button onClick={onClose} className="px-4 py-2 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-100">
              {t("common.cancel")}
            </button>
            <button data-testid="po-qty-submit-btn" onClick={submit} disabled={submitting || !hasChange || hasZeroOrNegative || reason.trim().length < 10}
              className="btn-primary-chizy px-5 py-2 rounded-xl text-sm flex items-center gap-1.5 disabled:opacity-50">
              <Send className="w-4 h-4" /> {submitting ? t("priceModal.sending") : t("priceModal.send")}
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
